import { Button, Popover, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getAwsAccountId, getAwsRegion } from "../../utils";

const GITHUB_SSH_RE = /^git@(github.com):(.*)\.git$/;
// best-effort attempt to distinguish files from directories:
// assume it's a file if it contains a period followed by something that isn't a period.
const IS_FILE_PATH = /^(.*\/)?[^/]*\.[^./]+$/;
const REMOVE_SLASH_PREFIX = /\/*(.*)/;
const PYTHON_PACKAGE_NAME = /^([A-Z0-9][A-Z0-9._-]*[A-Z0-9])/i; // https://packaging.python.org/en/latest/specifications/name-normalization/

export const reqSpecToUrls = (specString) => {
  //  Example:
  //    Input:
  //      ml-feature-set==0.1.25 ml-data-processor==0.2.0
  //    Output:
  //      ["https://us-west-2.console.aws.amazon.com/codesuite/codeartifact/d/478102786148/eonlabs/r/el-prediction-pipeline/p/pypi/ml-feature-set/v/0.1.25", "https://us-west-2.console.aws.amazon.com/codesuite/codeartifact/d/478102786148/eonlabs/r/el-prediction-pipeline/p/pypi/ml-data-processor/v/0.2.0"]
  const awsRegion = getAwsRegion();
  const accountId = getAwsAccountId();
  const specs = specString.split(/(\s+)/).filter((e) => e.trim().length > 0);
  return specs
    .map((s) => {
      const match = s.match(PYTHON_PACKAGE_NAME);
      if (match) {
        return `https://${awsRegion}.console.aws.amazon.com/codesuite/codeartifact/d/${accountId}/eonlabs/r/el-prediction-pipeline/p/pypi/${match[1]}/versions`;
      }
      return null;
    })
    .filter((url) => url !== null);
};

export const sourcesToURL = (parsed) => {
  //  Example:
  //    Input:
  //      feature_set_git_repo = git@github.com:Eon-Labs/ml-feature-set.git
  //      feature_set_branch = main
  //      feature_set_path = ml_feature_set/bundled/demo_feature_set.py
  //    Output:
  //      https://github.com/Eon-Labs/ml-feature-set/blob/main/ml_feature_set/bundled/demo_feature_set.py
  const match = parsed.feature_set_git_repo.match(GITHUB_SSH_RE);
  if (match) {
    const pathNoSlashPrefix = parsed.feature_set_git_path.match(REMOVE_SLASH_PREFIX)[1];
    if (IS_FILE_PATH.test(parsed.feature_set_git_path)) {
      return `https://${match[1]}/${match[2]}/blob/${parsed.feature_set_git_branch}/${pathNoSlashPrefix}`;
    } else {
      return `https://${match[1]}/${match[2]}/tree/${parsed.feature_set_git_branch}/${pathNoSlashPrefix}`;
    }
  }
  return null;
};

export const SubmissionSources = ({ sources }) => {
  const empty = <span style={{ color: "grey" }}>n/a</span>;
  if (!sources) {
    return empty;
  }

  const parsed = JSON.parse(sources);
  if (!parsed.feature_set_git_repo) {
    // empty or incomplete sources, e.g. because the task is not a feature set training task
    return empty;
  }

  let content;
  if (parsed instanceof Array) {
    // minimal support for multiple sources
    content = sources;
  } else {
    const repoUrl = sourcesToURL(parsed);
    const dataSource = Object.entries(parsed).map(([key, value]) => ({ key: key, value: value }));
    const columns = [
      {
        dataIndex: "key",
        key: "key",
      },
      {
        dataIndex: "value",
        key: "value",
        render: (value) => (
          <span
            style={{
              color: "green",
              fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace',
            }}
          >
            {value}
          </span>
        ),
      },
    ];
    content = (
      <>
        <Table dataSource={dataSource} columns={columns} pagination={false} showHeader={false} />
        {repoUrl && (
          <Button type="link" target="_blank" href={repoUrl}>
            View source
          </Button>
        )}
      </>
    );
  }

  return (
    <Popover content={content} trigger="click">
      <Button
        type="link"
        style={{
          width: "100%",
          height: "100%",
          padding: 0,
          display: "inline-block",
          color: "grey",
          overflow: "hidden",
        }}
      >
        <SearchOutlined />
      </Button>
    </Popover>
  );
};
